// AI Summary tab — CIVIX-native rolling summary from
// GET /api/leads/:id/ai-summary (tenant-scoped). Shows the narrative, warmth
// score, key facts, objections and the recommended next best action. No
// external enrichment — this is aggregated from the lead's own call summaries.

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, Flame, CheckCircle2, AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import { apiGet } from '../../../lib/api.js';

function WarmthBar({ score = 0 }) {
  const color = score >= 65 ? 'bg-green-500' : score >= 40 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <Flame className="w-4 h-4 text-civix-purpleSoft" />
      <div className="flex-1 h-2 rounded-full bg-civix-border overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-semibold text-white/80">{score}/100</span>
    </div>
  );
}

export default function AiSummaryTab({ leadId }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['lead-ai-summary', leadId],
    queryFn: () => apiGet(`/leads/${leadId}/ai-summary`),
    enabled: !!leadId,
  });

  if (isLoading) {
    return (
      <p className="text-xs text-white/40 p-3 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Building AI summary…
      </p>
    );
  }
  if (error) return <p className="text-xs text-red-400 p-3">Failed to load AI summary.</p>;

  const { summary, keyFacts = [], objections = [], nextBestAction, warmthScore = 0, callCount = 0 } = data || {};

  return (
    <div className="space-y-4 p-1 overflow-y-auto">
      <div className="rounded-lg bg-civix-panel border border-civix-border p-3 space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-white/85">
          <Sparkles className="w-4 h-4 text-civix-purpleSoft" />
          AI Summary
          <span className="ml-auto text-[10px] text-white/40 font-normal">
            {callCount} call{callCount === 1 ? '' : 's'}
          </span>
        </div>
        <p className="text-xs text-white/70 leading-relaxed">{summary}</p>
        <WarmthBar score={warmthScore} />
      </div>

      {nextBestAction && (
        <div className="rounded-lg bg-civix-purple/10 border border-civix-purple/30 p-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-civix-purpleSoft mb-1">
            <ArrowRight className="w-4 h-4" /> Next best action
          </div>
          <p className="text-xs text-white/80">{nextBestAction}</p>
        </div>
      )}

      {keyFacts.length > 0 && (
        <div>
          <h4 className="text-[11px] uppercase tracking-wide text-white/40 mb-1.5">Key facts</h4>
          <ul className="space-y-1">
            {keyFacts.map((f, i) => (
              <li key={i} className="flex gap-2 text-xs text-white/70">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {objections.length > 0 && (
        <div>
          <h4 className="text-[11px] uppercase tracking-wide text-white/40 mb-1.5">Objections</h4>
          <ul className="space-y-1">
            {objections.map((o, i) => (
              <li key={i} className="flex gap-2 text-xs text-white/70">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                <span>{o}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
